import { AnalyticsSender } from "@/api/Analytics";
import {
	FxIdDomainEntitiesPlayGameType,
	FxIdWebFeaturesGamesChooseGameResponsePublicGameData,
	FxIdWebFeaturesInfoNewsNews
} from "@/api/gen";
import { wrapStore } from "@/app/store/StoreWrapper";
import { produce } from "immer";
import type { StateCreator } from "zustand";
import { TorrentStateStatusesEnum } from "../utils/constants";

export enum appCodeEnum {
	aw = "aw",
	ww3 = "worldwar3",
	warface = "warface"
}

interface IFxApiSdkCache {
	appCode: string;
	clientGuid: string;
	apiType: string;
	cefVersion: string;
	appVersion: number;
	loaderName: string;
	hwProfileGuid: string;
	steamIsDeck: number;
}

export const defaultApp = appCodeEnum.aw;

export enum AuthMode {
	play = "play",
	oauth = "oauth",
	download = "download"
}
interface IAppStore {
	appCode: appCodeEnum;
	updateRequired: boolean;
	setUpdateRequired: (required: boolean) => void;
	gameConfig: FxIdWebFeaturesGamesChooseGameResponsePublicGameData;
	appCodeUpdated: boolean;
	appVersion: number;
	runningInLauncher: boolean;
	banChecked: boolean;
	setAppCode: (code: appCodeEnum, gameConfig: FxIdWebFeaturesGamesChooseGameResponsePublicGameData) => void;
	loaderName: string;
	setLoaderName: (name: string) => void;
	loading: boolean;
	error: string | null;
	news: FxIdWebFeaturesInfoNewsNews[];
	setNews: (news: FxIdWebFeaturesInfoNewsNews[]) => void;
	torrentStatus: TorrentStateStatusesEnum;
	setTorrentStatus: (status: TorrentStateStatusesEnum) => void;
	authMode: AuthMode;
	setAuthMode: (mode: AuthMode) => void;
	oauthRedirectUrl: string | null;
	oauthState: string | null;
	setOauthRedirectUrl: (url: string | null) => void;
	setOauthState: (state: string | null) => void;
	maintenance: boolean;
	setMaintenance: (maintenance: boolean) => void;

	fxApiSdkCache: IFxApiSdkCache | null;
	setFxApiSdkCache: (value: IFxApiSdkCache) => void;
	loginFlow: boolean;
	setLoginFlow: (loginFlow: boolean) => void;
	authToken: string | null;
	setAuthToken: (token: string | null) => void;
	storeModalOpen: boolean;
	setStoreModalOpen: (open: boolean) => void;
	settingsModalOpen: boolean;
	setSettingsModalOpen: (open: boolean) => void;
	supportModalOpen: boolean;
	setSupportModalOpen: (open: boolean) => void;
	setBanChecked: (checked: boolean) => void;
}

export const createAppStore: StateCreator<IAppStore> = (set) => ({
	maintenance: false,
	setBanChecked: (checked: boolean) => {
		set(
			produce((store: IAppStore) => {
				store.banChecked = checked;
			})
		);
	},
	banChecked: false,
	storeModalOpen: false,
	settingsModalOpen: false,
	supportModalOpen: false,
	setStoreModalOpen: (open: boolean) => {
		set(
			produce((store: IAppStore) => {
				store.storeModalOpen = open;
			})
		);
	},
	setSupportModalOpen: (open: boolean) => {
		set(
			produce((store: IAppStore) => {
				store.supportModalOpen = open;
			})
		);
	},
	setSettingsModalOpen: (open: boolean) => {
		set(
			produce((store: IAppStore) => {
				store.settingsModalOpen = open;
			})
		);
	},
	updateRequired: false,
	setUpdateRequired: (required: boolean) => {
		set(
			produce((store: IAppStore) => {
				store.updateRequired = required;
			})
		);
	},
	authToken: null,
	setAuthToken: (token: string | null) => {
		set(
			produce((store: IAppStore) => {
				store.authToken = token;
			})
		);
	},
	loginFlow: false,
	setLoginFlow: (loginFlow: boolean) => {
		set(
			produce((store: IAppStore) => {
				store.loginFlow = loginFlow;
			})
		);
	},
	setMaintenance: (maintenance: boolean) => {
		set(
			produce((store: IAppStore) => {
				store.maintenance = maintenance;
			})
		);
	},

	fxApiSdkCache: null,

	setFxApiSdkCache: (value: IFxApiSdkCache) => {
		set(
			produce((store: IAppStore) => {
				store.fxApiSdkCache = value;
				store.runningInLauncher = value.appVersion > 0;
				store.appVersion = value.appVersion;
				AnalyticsSender.BuildId = value.appVersion;
			})
		);
	},

	torrentStatus: TorrentStateStatusesEnum.finished,
	oauthRedirectUrl: null,
	oauthState: null,

	setOauthRedirectUrl: (url: string | null) => {
		set(
			produce((store: IAppStore) => {
				store.oauthRedirectUrl = url;
			})
		);
	},
	setOauthState: (state: string | null) => {
		set(
			produce((store: IAppStore) => {
				store.oauthState = state;
			})
		);
	},
	authMode: AuthMode.play,
	setAuthMode: (mode: AuthMode) => {
		set(
			produce((store: IAppStore) => {
				store.authMode = mode;
			})
		);
	},
	setTorrentStatus: (status: TorrentStateStatusesEnum) => {
		set(
			produce((store: IAppStore) => {
				store.torrentStatus = status;
			})
		);
	},
	news: [],
	setNews: (news: FxIdWebFeaturesInfoNewsNews[]) => {
		set(
			produce((store: IAppStore) => {
				store.news = news;
			})
		);
	},
	appCode: defaultApp,
	gameConfig: {
		PlatformData: {},
		GameOrientation: 0,
		MobileGameOrientation: 0,
		SupportedLanguages: [],
		DefaultLanguage: "",
		FxLauncherOptions: {
			AdditionalLauncherArguments: "",
			GameSearchPaths: []
		},
		Store: {
			XsollaEnabled: false,
			YookassaEnabled: false,
			StripeEnabled: false
		},
		CompanyName: "",
		TosUrl: "",
		TosUrlOpenMethod: 0,
		PpUrl: "",
		PpUrlOpenMethod: 0,
		PublicWebClientConfig: {
			CardImageUrl: "",
			FullLogoUrl: "",
			SplashImageUrl: "",
			Background: null,
			SmallLogoUrl: "",
			HeaderLogo: null,
			IconUrl: "",
			ExternalPlayUrl: ``,
			PromoEnabled: false
		},
		Turnstile: {
			SiteKey: ""
		},
		GameSystemName: defaultApp,
		NamespacedProductsL10N: true,
		PlayGameType: FxIdDomainEntitiesPlayGameType.NUMBER_unknown_default_open_api
	},
	appCodeUpdated: false,
	runningInLauncher: false,
	appVersion: -1,
	loaderName: "",
	error: null,
	loading: false,

	setAppCode: (code: appCodeEnum, gameConfig: FxIdWebFeaturesGamesChooseGameResponsePublicGameData) => {
		set(
			produce((store: IAppStore) => {
				store.appCode = code;
				store.gameConfig = gameConfig;
				store.appCodeUpdated = true;
			})
		);
	},
	setLoaderName: (name: string) => {
		set(
			produce((store: IAppStore) => {
				store.loaderName = name;
			})
		);
	}
});

export const appStoreWrapper = wrapStore<IAppStore>();
export const appStore = appStoreWrapper.withStore(createAppStore, "app-store");
